import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useQuery } from "react-query";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import {
  ArrowNarrowLeftIcon,
  ArrowNarrowRightIcon,
} from "@heroicons/react/solid";
import { format } from "date-fns";

import { KTGet } from "@/kyna/lib/db";

import HeaderNav from "../../includes/HeaderNav";
import Footer from "../../includes/Footer";
import BookingSec from "./BookingCTA";
import Spinner from "@/kyna/ui/preloader/Spinner";
import KNPagination from "@/kyna/ui/pagination/KNPagination";

const NewsEvents = () => {
  const router = useRouter();
  const itemsPerPage = 15;
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [skipStart, setSkipStart] = useState(1);
  const [skipEnd, setSkipEnd] = useState(itemsPerPage);
  const {
    data: CategoryData,
    status: CategoryDataStatus,
    isLoading: CategoryIsLoading,
  } = useQuery("CategoryData", async () => {
    return await KTGet("/api/events/public/categories");
  });

  const {
    data: EventsData,
    status: EventsDataStatus,
    isLoading: EventsIsLoading,
    refetch: EventsIsRefetch,
  } = useQuery(
    ["EventsData", router.query],
    async () => {
      return await KTGet("/api/events/public/", {
        current_date: new Date(),
        orderBy: "post_date,desc",
        category: router?.query?.category ? router?.query?.category : "",
        limit: itemsPerPage,
        page: router?.query?.page,
      });
    },
    {
      enabled:
        CategoryDataStatus === "success" &&
        !isEmpty(CategoryData?.data?.result),
    }
  );

  const {
    data: FeaturedPostData,
    status: FeaturedPostStatus,
    isLoading: FeaturedPostIsLoading,
  } = useQuery(["FeaturedPost"], async () => {
    return await KTGet("/api/events/public/", {
      limit: itemsPerPage,
      current_date: new Date(),
      orderBy: "post_date,desc",
      featured: true,
    });
  });

  // add the section id of "blog-content" on use router
  const handlePageClick = (event) => {
    const pageNumber = event.selected + 1;
    let url = `/news-and-events?page=${pageNumber}`;
    if (!isEmpty(router?.query?.category)) {
      url += `&category=${router.query.category}#blog-section`;
    } else {
      url += `#blog-section`;
    }
    router.push(url);
    const totalLoaded = pageNumber * itemsPerPage;
    setSkipStart(totalLoaded - (itemsPerPage - 1));
    if (totalLoaded > totalCount) {
      setSkipEnd(totalCount);
    } else {
      setSkipEnd(totalLoaded);
    }
  };

  useEffect(() => {
    const totalCountRes = parseInt(EventsData?.data?.info?.totalCount)
      ? parseInt(EventsData.data.info.totalCount)
      : 0;
    const NewPageCount = Math.ceil(totalCountRes / itemsPerPage);
    setTotalCount(totalCountRes);
    setPageCount(NewPageCount);
    if (totalCountRes && skipEnd > totalCountRes) {
      setSkipEnd(totalCountRes);
    }
    router?.query?.page
      ? setSkipStart(router?.query?.page * itemsPerPage - (itemsPerPage - 1))
      : setSkipStart(1);
  }, [router.query, EventsData?.data?.result]);

  return (
    <>
      {/* MAIN NAV */}
      <>
        <HeaderNav />
      </>

      {/* NEWS AND EVENTS HEADER */}
      <section className="mt-44">
        <div className="max-w-5xl mx-auto mt-16 px-5">
          <div className="section-title border-b border-b-gray-300 mb-10">
            <h1 className="mb-10 sm:max-w-xl mx-auto leading-normal text-center">
              News and Events
            </h1>
          </div>
        </div>
      </section>
      {/* FEATURED POST */}
      <section className="max-w-screen-2xl mx-auto sm:px-10 px-8 pb-16">
        <div className="grid lg:grid-cols-3 grid-cols-1 gap-10 lg:max-w-full max-w-xl mx-auto">
          {!isEmpty(FeaturedPostData?.data?.result) ? (
            FeaturedPostData?.data?.result?.map((i) => {
              return (
                <Link
                  key={i.id}
                  passHref
                  className="cursor-pointer"
                  href={`/news-and-events/${i.slug}`}
                >
                  <div className="col-span-1 cursor-pointer bg-white rounded-xl d-flex justify-items-stretch transition duration-300 ease-in-out shadow-md">
                    <div className="">
                      <div className="relative w-full xl:h-[280px] lg:h-[230px] sm:h-[280px] h-[250px]">
                        {i?.media?.path ? (
                          <Image
                            className="cursor-pointer object-cover rounded-t-xl"
                            src={i?.media?.path}
                            alt="News and Events"
                            layout="fill"
                          />
                        ) : null}
                      </div>
                      <div className="relative">
                        <p className="text-white font-semibold uppercase absolute right-0 bg-[#95d50b] py-1 px-4">
                          {format(new Date(i.post_date), "PP")}
                        </p>
                        <div className="py-8 px-8">
                          <h3 className="text-lg pt-6">{i.title}</h3>
                          <p className="">{i.excerpt}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          ) : (
            <></>
          )}
        </div>
      </section>

      {/* BLOG LIST */}
      <section id="blog-section" className="bg-gray-100 pt-16 pb-24">
        <div className="max-w-screen-2xl mx-auto sm:px-10 px-8">
          <div className="grid grid-cols-6">
            {/* BLOG CATEGORIES */}
            <div className="col-span-1">
              <h2 className="text-sm uppercase text-gray-400 tracking-widest">
                Categories
              </h2>
              <p
                className={`cursor-pointer text-lg mb-2 ${
                  !router?.query?.category ? "text-blue font-semibold" : ""
                }`}
                onClick={() =>
                  router.push(`/news-and-events?page=1#blog-section`)
                }
                key="All"
              >
                All
              </p>
              {CategoryData?.data?.result?.map((i, index) => {
                return (
                  <p
                    className={`cursor-pointer text-lg mb-2 ${
                      router.query.category === i?.title
                        ? "text-blue font-semibold"
                        : ""
                    }`}
                    onClick={() =>
                      router.push(
                        `/news-and-events?category=${i?.title}&page=1#blog-section`
                      )
                    }
                    key={i.id}
                  >
                    {i.title}
                  </p>
                );
              })}
            </div>

            {/* BLOG LIST */}
            <div className="col-span-5">
              <section className="grid lg:grid-cols-3 grid-cols-1 gap-10 lg:max-w-full max-w-xl mx-auto">
                {EventsIsLoading ? (
                  <Spinner
                    isLoading={EventsIsLoading}
                    svgClass="mb-20 !text-gray-500"
                  />
                ) : (
                  <>
                    {!isEmpty(EventsData?.data?.result) ? (
                      EventsData?.data?.result?.map((events, index) => {
                        return (
                          <>
                            <Link
                              key={events.id}
                              passHref
                              className="cursor-pointer"
                              href={`/news-and-events/${events?.slug}`}
                            >
                              <div className="col-span-1 cursor-pointer bg-white rounded-xl d-flex justify-items-stretch transition duration-300 ease-in-out hover:shadow-2xl">
                                <div className="">
                                  <div className="relative w-full xl:h-[280px] lg:h-[230px] sm:h-[280px] h-[250px]">
                                    {events?.media?.path ? (
                                      <Image
                                        className="cursor-pointer object-cover rounded-t-xl"
                                        src={events?.media?.path}
                                        alt="News and Events"
                                        layout="fill"
                                      />
                                    ) : null}
                                  </div>
                                  <div className="relative">
                                    <p className="text-white font-semibold uppercase absolute right-0 bg-[#95d50b] py-1 px-4">
                                      {format(new Date(events.post_date), "PP")}
                                    </p>
                                    <div className="py-8 px-8">
                                      <h3 className="text-lg pt-6">
                                        {events.title}
                                      </h3>
                                      <p className="">{events.excerpt}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </>
                        );
                      })
                    ) : (
                      <div className="px-2 mb-20">
                        <div className="text-gray-500">No blogs posted.</div>
                      </div>
                    )}
                  </>
                )}
              </section>
              {/* Pagination */}
              {totalCount > 15 && (
                <section className="mt-10 py-5 px-5 xl:px-8 2xl:px-10">
                  <KNPagination
                    pageCount={pageCount}
                    skipStart={skipStart}
                    skipEnd={
                      EventsData?.data?.info?.resultCount + (skipStart - 1)
                    }
                    totalCount={totalCount}
                    initialPage={
                      router.query?.page > 1 ? router.query.page - 1 : undefined
                    }
                    handlePageClick={handlePageClick}
                  />
                </section>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING CTA */}
      <BookingSec />

      {/* FOOTER */}
      <Footer />
    </>
  );
};

export default NewsEvents;
